import type { CmmdProjectTaskDraftProposal, CmmdProjectTaskDraftTask } from "@cmmd-forge/contracts";

type EditableTaskFields = Pick<
  CmmdProjectTaskDraftTask,
  | "title"
  | "goal"
  | "description"
  | "acceptanceCriteria"
  | "dependencyRefs"
  | "priority"
  | "workItemType"
>;

const EMPTY_ACCEPTANCE_CRITERION =
  "Project Lead will refine and confirm this completion criterion.";
const MAX_ACCEPTANCE_CRITERIA = 20;
const MAX_ACCEPTANCE_CRITERION_LENGTH = 1_000;
const MAX_TASK_TITLE_LENGTH = 500;
const MAX_TASK_DESCRIPTION_LENGTH = 5_000;
const SPLIT_TITLE_SUFFIX = " (part 2)";
export const MAX_TASKS_PER_PROPOSAL = 50;
export const MAX_DEPENDENCIES_PER_TASK = 20;

export function normalizeRequiredTaskText(
  value: string,
  fallback: string,
  maxLength: number,
  label: string,
): { readonly value: string; readonly feedback: string | null } {
  const trimmed = value.trim();
  if (trimmed.length === 0) return { value: fallback, feedback: null };
  if (trimmed.length <= maxLength) return { value: trimmed, feedback: null };
  return {
    value: trimmed.slice(0, maxLength),
    feedback: `${label} is limited to ${maxLength.toLocaleString("en-US")} characters; the extra text was removed.`,
  };
}

export function normalizeAcceptanceCriteriaInput(value: string): {
  readonly criteria: string[];
  readonly feedback: string | null;
} {
  const enteredCriteria = value
    .split("\n")
    .map((criterion) => criterion.trim())
    .filter(Boolean);
  const tooMany = enteredCriteria.length > MAX_ACCEPTANCE_CRITERIA;
  const tooLong = enteredCriteria.some(
    (criterion) => criterion.length > MAX_ACCEPTANCE_CRITERION_LENGTH,
  );
  const criteria = enteredCriteria
    .slice(0, MAX_ACCEPTANCE_CRITERIA)
    .map((criterion) => criterion.slice(0, MAX_ACCEPTANCE_CRITERION_LENGTH));
  const feedback = [
    tooMany ? "Only the first 20 acceptance criteria were kept." : null,
    tooLong ? "Acceptance criteria are limited to 1,000 characters each." : null,
  ]
    .filter((message): message is string => message !== null)
    .join(" ");
  return {
    criteria: criteria.length > 0 ? criteria : [EMPTY_ACCEPTANCE_CRITERION],
    feedback: feedback || null,
  };
}

export function normalizeAcceptanceCriteria(value: string): string[] {
  return normalizeAcceptanceCriteriaInput(value).criteria;
}

export function updateTask(
  proposal: CmmdProjectTaskDraftProposal,
  localRef: string,
  changes: Partial<EditableTaskFields>,
): CmmdProjectTaskDraftProposal {
  return {
    ...proposal,
    tasks: proposal.tasks.map((task) =>
      task.localRef === localRef ? { ...task, ...changes } : task,
    ),
  };
}

export function updateTaskDependencies(
  proposal: CmmdProjectTaskDraftProposal,
  localRef: string,
  dependencyRef: string,
  checked: boolean,
): CmmdProjectTaskDraftProposal {
  const task = proposal.tasks.find((candidate) => candidate.localRef === localRef);
  if (task === undefined) return proposal;
  if (
    checked &&
    !task.dependencyRefs.includes(dependencyRef) &&
    task.dependencyRefs.length >= MAX_DEPENDENCIES_PER_TASK
  ) {
    return proposal;
  }
  return updateTask(proposal, localRef, {
    dependencyRefs: checked
      ? [...task.dependencyRefs, dependencyRef]
      : task.dependencyRefs.filter((dependency) => dependency !== dependencyRef),
  });
}

export function addSubtask(
  proposal: CmmdProjectTaskDraftProposal,
  parentTaskRef: string,
  localRef: string,
): CmmdProjectTaskDraftProposal {
  if (proposal.tasks.length >= MAX_TASKS_PER_PROPOSAL) return proposal;
  const parent = proposal.tasks.find((task) => task.localRef === parentTaskRef);
  if (!parent) return proposal;
  const task: CmmdProjectTaskDraftTask = {
    localRef,
    title: "New subtask",
    goal: "Define the outcome for this subtask",
    description: "Add implementation context for this subtask.",
    acceptanceCriteria: ["Define a verifiable completion criterion."],
    parentTaskRef,
    dependencyRefs: [],
    priority: parent.priority,
    workItemType: parent.workItemType,
    designReferences: [],
  };
  return { ...proposal, tasks: [...proposal.tasks, task] };
}

export function splitTask(
  proposal: CmmdProjectTaskDraftProposal,
  localRef: string,
  newLocalRef: string,
): CmmdProjectTaskDraftProposal {
  if (proposal.tasks.length >= MAX_TASKS_PER_PROPOSAL) return proposal;
  const index = proposal.tasks.findIndex((task) => task.localRef === localRef);
  const source = proposal.tasks[index];
  if (!source) return proposal;
  const task: CmmdProjectTaskDraftTask = {
    ...source,
    localRef: newLocalRef,
    title: `${source.title.slice(0, MAX_TASK_TITLE_LENGTH - SPLIT_TITLE_SUFFIX.length)}${SPLIT_TITLE_SUFFIX}`,
    acceptanceCriteria: ["Define a verifiable completion criterion for this split task."],
    dependencyRefs: [source.localRef],
  };
  return {
    ...proposal,
    tasks: [...proposal.tasks.slice(0, index + 1), task, ...proposal.tasks.slice(index + 1)],
  };
}

export function moveTask(
  proposal: CmmdProjectTaskDraftProposal,
  localRef: string,
  direction: -1 | 1,
): CmmdProjectTaskDraftProposal {
  const index = proposal.tasks.findIndex((task) => task.localRef === localRef);
  const targetIndex = index + direction;
  if (index < 0 || targetIndex < 0 || targetIndex >= proposal.tasks.length) return proposal;
  const tasks = [...proposal.tasks];
  const [task] = tasks.splice(index, 1);
  if (!task) return proposal;
  tasks.splice(targetIndex, 0, task);
  return { ...proposal, tasks };
}

export function removeTaskBranch(
  proposal: CmmdProjectTaskDraftProposal,
  localRef: string,
): CmmdProjectTaskDraftProposal {
  const removedRefs = new Set([localRef]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const task of proposal.tasks) {
      if (task.parentTaskRef !== null && removedRefs.has(task.parentTaskRef)) {
        if (!removedRefs.has(task.localRef)) changed = true;
        removedRefs.add(task.localRef);
      }
    }
  }
  const tasks: CmmdProjectTaskDraftTask[] = [];
  for (const task of proposal.tasks) {
    if (removedRefs.has(task.localRef)) continue;
    tasks.push({
      ...task,
      dependencyRefs: task.dependencyRefs.filter((dependency) => !removedRefs.has(dependency)),
    });
  }
  return { ...proposal, tasks };
}

export function createInitialProposal(input: {
  readonly projectId: number;
  readonly title: string;
  readonly description: string;
  readonly localRef: string;
  readonly priority: CmmdProjectTaskDraftTask["priority"];
  readonly workItemType: CmmdProjectTaskDraftTask["workItemType"];
}): CmmdProjectTaskDraftProposal {
  const description =
    input.description.trim().slice(0, MAX_TASK_DESCRIPTION_LENGTH) ||
    "Project Lead will add the task context.";
  const title = input.title.trim().slice(0, MAX_TASK_TITLE_LENGTH) || "New task tree";
  return {
    schemaVersion: 1,
    projectId: input.projectId,
    goal: title,
    tasks: [
      {
        localRef: input.localRef,
        title,
        goal: title,
        description,
        acceptanceCriteria: ["Project Lead will refine and confirm the completion criteria."],
        parentTaskRef: null,
        dependencyRefs: [],
        priority: input.priority,
        workItemType: input.workItemType,
        designReferences: [],
      },
    ],
  };
}
